import React, { useState } from "react";
import { SvgIcon } from "../SvgIcon";
import { t, Locale } from "../../i18n";

interface ChannelState {
  email: boolean;
  sms: boolean;
}

type NotifSettings = Record<string, ChannelState>;
type ReportFrequency = "off" | "weekly" | "monthly" | "quarterly" | "yearly";

interface NotifRowConfig {
  key: string;
  title: string;
  description: string;
  emailLocked?: "always" | "conditional";
  smsAvailable?: boolean;
  emailOnly?: boolean;
  canDisable?: boolean;
  // When true, both Email and SMS columns show a "Required" label instead of toggles
  required?: boolean;
}

const securityAlertRows: NotifRowConfig[] = [
  { key: "recoveryAttemptStarted", title: "Recovery attempt started", description: "", emailLocked: "conditional" },
  { key: "recoveryAttemptCancelled", title: "Recovery attempt cancelled", description: "", emailLocked: "conditional" },
  { key: "passwordChangedViaRecovery", title: "Password changed via recovery", description: "", emailLocked: "always" },
  { key: "newDeviceLogin", title: "New device login", description: "", emailLocked: "conditional" },
  { key: "loginFromNewLocation", title: "Login from new location", description: "", emailLocked: "conditional" },
  { key: "accountStatusChanged", title: "Account status changed", description: "", emailLocked: "conditional" }
];

// UI-only: these "Required" labels are a UI promise pending backend enforcement.
// The notification sending logic must be updated to always send these regardless
// of user preferences once the notification system is implemented.
// Tracked in ROADMAP.md.
const criticalSecurityRows: NotifRowConfig[] = [
  { key: "passwordChanged", title: "Password changed", description: "", required: true },
  { key: "twoFaDisabled", title: "Two-factor authentication disabled", description: "", required: true },
  { key: "recoveryEmailChanged", title: "Recovery email changed or removed", description: "", required: true },
  { key: "recoveryPhoneChanged", title: "Recovery phone changed or removed", description: "", required: true },
  { key: "identityVerificationRevoked", title: "ID/Passport verification revoked", description: "", required: true },
  { key: "sessionRevokedRemotely", title: "Active session revoked from another device", description: "", required: true }
];

const accountAlertRows: NotifRowConfig[] = [
  { key: "sendingLimit60", title: "Sending limit at 60%", description: "" },
  { key: "sendingLimit90", title: "Sending limit at 90%", description: "" },
  { key: "limitExtensionGranted", title: "Sending limit increased", description: "", smsAvailable: false },
  { key: "trustTierUpgraded", title: "Account standing improved", description: "", smsAvailable: false },
  { key: "sendingRestrictionsLifted", title: "Account standing changed", description: "" }
];

const billingRows: NotifRowConfig[] = [
  { key: "paymentSuccessful", title: "Payment successful", description: "", emailOnly: true, canDisable: true },
  { key: "paymentFailed", title: "Payment failed", description: "", emailOnly: true, canDisable: false },
  { key: "subscriptionRenewing", title: "Subscription renewing in 7 days", description: "", emailOnly: true, canDisable: true },
  { key: "subscriptionCancelled", title: "Subscription cancelled", description: "", emailOnly: true, canDisable: false },
  { key: "freeTrialEnding", title: "Free trial ending in 3 days", description: "", emailOnly: true, canDisable: false }
];

// Mock data: phone not configured
const hasPhone = false;

const mockDefaults: NotifSettings = {
  recoveryAttemptStarted: { email: true, sms: false },
  recoveryAttemptCancelled: { email: true, sms: false },
  passwordChangedViaRecovery: { email: true, sms: false },
  newDeviceLogin: { email: true, sms: false },
  loginFromNewLocation: { email: true, sms: false },
  accountStatusChanged: { email: true, sms: false },
  sendingLimit60: { email: true, sms: false },
  sendingLimit90: { email: true, sms: false },
  limitExtensionGranted: { email: true, sms: false },
  trustTierUpgraded: { email: true, sms: false },
  sendingRestrictionsLifted: { email: true, sms: false },
  paymentSuccessful: { email: true, sms: false },
  paymentFailed: { email: true, sms: false },
  subscriptionRenewing: { email: true, sms: false },
  subscriptionCancelled: { email: true, sms: false },
  freeTrialEnding: { email: true, sms: false }
};

function Toggle({
  on,
  disabled,
  onChange,
  title
}: {
  on: boolean;
  disabled: boolean;
  onChange: (val: boolean) => void;
  title: string;
}) {
  return (
    <div
      role="switch"
      aria-checked={on}
      title={title}
      onClick={() => !disabled && onChange(!on)}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1,
        transition: "opacity 0.2s"
      }}
    >
      <div className={`imgTrustSwitch ${on ? "imgTrustSwitchOn" : ""}`}>
        <div className="imgTrustKnob" />
      </div>
    </div>
  );
}

function InlineWarning({ message }: { message: string }) {
  return (
    <div
      style={{
        marginTop: "8px",
        padding: "8px 12px",
        background: "rgba(245, 158, 11, 0.1)",
        borderLeft: "3px solid #f59e0b",
        borderRadius: "4px",
        fontSize: "12px",
        color: "#f59e0b",
        fontWeight: 500
      }}
    >
      {message}
    </div>
  );
}

function SavedFlash({ visible, locale }: { visible: boolean; locale: Locale }) {
  if (!visible) return null;
  return (
    <div style={{ fontSize: "13px", color: "#10b981", fontWeight: 500 }}>
      {t(locale, "notifications.saved")}
    </div>
  );
}

function LockIcon() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "16px", height: "16px" }}>
      <SvgIcon name="lock" title="Required — cannot be disabled" />
    </span>
  );
}

// Shared grid for all notification rows and section headers.
// Col 1: label (flexible), Col 2: Email (fixed), Col 3: SMS (fixed).
// Billing rows leave Col 3 empty, but the same template keeps Email aligned.
const ROW_GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 64px 64px",
  alignItems: "center"
};

const COL_CELL: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center"
};

function NotifRow({
  config,
  settings,
  onToggle,
  savedKey,
  blockWarning,
  showEmailOnlyBadge,
  disableSms,
  locale
}: {
  config: NotifRowConfig;
  settings: ChannelState;
  onToggle: (key: string, channel: "email" | "sms", value: boolean) => void;
  savedKey: string | null;
  blockWarning: string | null;
  showEmailOnlyBadge: boolean;
  disableSms: boolean;
  locale: Locale;
}) {
  const isLocked = config.canDisable === false;

  // Determine what goes in each column cell
  let emailCell: React.ReactNode;
  let smsCell: React.ReactNode = null;

  if (config.required) {
    // Critical Security Events: lock icon in both columns
    emailCell = <LockIcon />;
    smsCell = <LockIcon />;
  } else if (config.emailOnly) {
    // Billing rows: toggle or lock in email column only; SMS cell stays empty
    emailCell = isLocked ? (
      <LockIcon />
    ) : (
      <Toggle
        on={settings.email}
        disabled={false}
        onChange={(val) => onToggle(config.key, "email", val)}
        title=""
      />
    );
  } else {
    // Regular rows: email toggle in col 2, SMS toggle or EMAIL ONLY badge in col 3
    emailCell = (
      <Toggle
        on={settings.email}
        disabled={
          config.emailLocked === "always"
            ? !settings.email
            : config.emailLocked === "conditional"
              ? !settings.email && !settings.sms
              : false
        }
        onChange={(val) => onToggle(config.key, "email", val)}
        title={
          config.emailLocked === "always" && !settings.email
            ? t(locale, "notifications.alert.emailLocked.always")
            : config.emailLocked === "conditional" && !settings.email && !settings.sms
              ? t(locale, "notifications.alert.emailLocked.conditional")
              : ""
        }
      />
    );
    smsCell = showEmailOnlyBadge && config.smsAvailable === false ? (
      <div
        style={{
          fontSize: "11px",
          fontWeight: 600,
          color: "var(--muted)",
          padding: "3px 6px",
          background: "var(--hover-bg)",
          borderRadius: "3px",
          textTransform: "uppercase" as const,
          letterSpacing: "0.3px",
          textAlign: "center" as const
        }}
      >
        {t(locale, "notifications.emailOnly")}
      </div>
    ) : (
      <Toggle
        on={settings.sms}
        disabled={disableSms || config.smsAvailable === false}
        onChange={(val) => onToggle(config.key, "sms", val)}
        title={
          disableSms
            ? t(locale, "notifications.alert.smsDisabled")
            : config.smsAvailable === false
              ? t(locale, "notifications.alert.smsUnavailable")
              : ""
        }
      />
    );
  }

  return (
    <div>
      <div style={{ ...ROW_GRID, paddingBottom: "12px", marginBottom: blockWarning ? "8px" : "0" }}>
        <div>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "2px" }}>
            {config.title}
          </div>
          {config.description && (
            <div style={{ fontSize: "12px", color: "var(--muted)" }}>
              {config.description}
            </div>
          )}
        </div>
        <div style={COL_CELL}>{emailCell}</div>
        <div style={COL_CELL}>{smsCell}</div>
      </div>

      {blockWarning && <InlineWarning message={blockWarning} />}
      {savedKey === config.key && <SavedFlash visible={true} locale={locale} />}
    </div>
  );
}

export function NotificationsSettings(props: { locale: Locale }) {
  const locale = props.locale;
  const [settings, setSettings] = useState<NotifSettings>(mockDefaults);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [blockWarnings, setBlockWarnings] = useState<Record<string, string | null>>({});
  const [reportFrequency, setReportFrequency] = useState<ReportFrequency>("weekly");

  const handleToggle = (key: string, channel: "email" | "sms", value: boolean) => {
    const current = settings[key];

    // Check if this is a security alert row
    const isSecurityAlert = securityAlertRows.some((row) => row.key === key);

    if (channel === "email" && !value && isSecurityAlert) {
      // Trying to disable email on a security alert
      if (current.sms === false) {
        // SMS is also off, so block and show warning
        setBlockWarnings((prev) => ({
          ...prev,
          [key]: t(locale, "notifications.alert.blockWarning")
        }));
        setTimeout(() => {
          setBlockWarnings((prev) => ({
            ...prev,
            [key]: null
          }));
        }, 3000);
        return;
      }
    }

    // Clear any warning for this key on allowed toggle
    if (channel === "sms" && value) {
      setBlockWarnings((prev) => ({
        ...prev,
        [key]: null
      }));
    }

    // Update setting
    setSettings((prev) => ({
      ...prev,
      [key]: {
        ...current,
        [channel]: value
      }
    }));

    // Show saved feedback
    setSavedKey(key);
    setTimeout(() => setSavedKey(null), 1500);
  };

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "4px" }}>{t(locale, "notifications.title")}</h2>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: "24px" }}>
        {t(locale, "notifications.description")}
      </p>

      {/* SMS not available banner */}
      {!hasPhone && (
        <div
          style={{
            marginBottom: "20px",
            padding: "12px 14px",
            background: "rgba(245, 158, 11, 0.1)",
            borderLeft: "3px solid #f59e0b",
            borderRadius: "6px",
            fontSize: "13px",
            color: "var(--text)"
          }}
        >
          <span style={{ fontWeight: 600 }}>{t(locale, "notifications.smsUnavailable")}</span>
          <span style={{ color: "var(--muted)" }}>{t(locale, "notifications.smsUnavailable.hint")}</span>
        </div>
      )}

      {/* Security Alerts Card */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "notifications.securityAlerts.title")}</h3>

        {/* Column headers */}
        <div style={{ ...ROW_GRID, paddingBottom: "12px", marginBottom: "12px", borderBottom: "1px solid var(--line)", opacity: 0.6 }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)" }}>{t(locale, "notifications.col.alert")}</div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", textAlign: "center" }}>{t(locale, "notifications.col.email")}</div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", textAlign: "center" }}>{t(locale, "notifications.col.sms")}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {securityAlertRows.map((row) => (
            <NotifRow
              key={row.key}
              config={row}
              settings={settings[row.key]}
              onToggle={handleToggle}
              savedKey={savedKey}
              blockWarning={blockWarnings[row.key] || null}
              showEmailOnlyBadge={false}
              disableSms={!hasPhone}
              locale={locale}
            />
          ))}
        </div>
      </div>

      {/* Critical Security Events Card */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "4px" }}>{t(locale, "notifications.criticalEvents.title")}</h3>
        <p style={{ fontSize: "12px", color: "var(--muted)", marginTop: 0, marginBottom: "16px", lineHeight: "1.5" }}>
          {t(locale, "notifications.criticalEvents.description")}
        </p>

        {/* Column headers */}
        <div style={{ ...ROW_GRID, paddingBottom: "12px", marginBottom: "12px", borderBottom: "1px solid var(--line)", opacity: 0.6 }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)" }}>{t(locale, "notifications.col.event")}</div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", textAlign: "center" }}>{t(locale, "notifications.col.email")}</div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", textAlign: "center" }}>{t(locale, "notifications.col.sms")}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {criticalSecurityRows.map((row) => (
            <NotifRow
              key={row.key}
              config={row}
              settings={{ email: true, sms: true }}
              onToggle={handleToggle}
              savedKey={null}
              blockWarning={null}
              showEmailOnlyBadge={false}
              disableSms={false}
              locale={locale}
            />
          ))}
        </div>
      </div>

      {/* Account Alerts Card */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "notifications.accountAlerts.title")}</h3>

        {/* Column headers */}
        <div style={{ ...ROW_GRID, paddingBottom: "12px", marginBottom: "12px", borderBottom: "1px solid var(--line)", opacity: 0.6 }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)" }}>{t(locale, "notifications.col.alert")}</div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", textAlign: "center" }}>{t(locale, "notifications.col.email")}</div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", textAlign: "center" }}>{t(locale, "notifications.col.sms")}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {accountAlertRows.map((row) => (
            <NotifRow
              key={row.key}
              config={row}
              settings={settings[row.key]}
              onToggle={handleToggle}
              savedKey={savedKey}
              blockWarning={blockWarnings[row.key] || null}
              showEmailOnlyBadge={true}
              disableSms={!hasPhone}
              locale={locale}
            />
          ))}
        </div>
      </div>

      {/* Billing Alerts Card */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "notifications.billingAlerts.title")}</h3>

        {/* Column headers — same 3-column grid; SMS header intentionally blank for billing-only section */}
        <div style={{ ...ROW_GRID, paddingBottom: "12px", marginBottom: "12px", borderBottom: "1px solid var(--line)", opacity: 0.6 }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)" }}>{t(locale, "notifications.col.alert")}</div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", textAlign: "center" }}>{t(locale, "notifications.col.email")}</div>
          <div />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {billingRows.map((row) => (
            <NotifRow
              key={row.key}
              config={row}
              settings={settings[row.key]}
              onToggle={handleToggle}
              savedKey={savedKey}
              blockWarning={blockWarnings[row.key] || null}
              showEmailOnlyBadge={false}
              disableSms={!hasPhone}
              locale={locale}
            />
          ))}
        </div>
      </div>

      {/* Account Summary Report Card */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "notifications.summaryReport.title")}</h3>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "4px" }}>{t(locale, "notifications.summaryReport.frequency")}</div>
            <div style={{ fontSize: "12px", color: "var(--muted)" }}>
              {t(locale, `notifications.frequency.${reportFrequency}`)}
            </div>
          </div>
          <select
            value={reportFrequency}
            onChange={(e) => setReportFrequency(e.target.value as ReportFrequency)}
            style={{
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--control-border)",
              borderRadius: "6px",
              padding: "6px 10px",
              fontSize: "13px",
              cursor: "pointer",
              minWidth: "120px"
            }}
          >
            <option value="off">{t(locale, "notifications.frequency.option.off")}</option>
            <option value="weekly">{t(locale, "notifications.frequency.option.weekly")}</option>
            <option value="monthly">{t(locale, "notifications.frequency.option.monthly")}</option>
            <option value="quarterly">{t(locale, "notifications.frequency.option.quarterly")}</option>
            <option value="yearly">{t(locale, "notifications.frequency.option.yearly")}</option>
          </select>
        </div>
      </div>

      {/* Info note */}
      <div style={{ marginTop: "16px", padding: "12px", background: "rgba(96, 165, 250, 0.1)", borderRadius: "6px", borderLeft: "3px solid var(--accent)" }}>
        <div style={{ fontSize: "12px", color: "var(--muted)", lineHeight: "1.5" }}>
          {t(locale, "notifications.autoSaved")}
        </div>
      </div>
    </div>
  );
}
